import React, { useState } from "react";
import { Link } from "react-router-dom";
import styles from "./styles.module.scss";
import { cls } from "app/utils";
import { schedulingFetchPost } from "app/utils/request/scheduling/fetch";
import { formatServerAmount } from "app/utils/currency";
import ToolTip from "app/routes/Scheduling/ToolTip";
import { useTranslation } from "react-i18next";
import { currency_symbol, offeringLimitNote, scheduledFirstChargeNote, weekdayRestrictionLabel } from "../NewOffering/utils";
import PackageDetailsModal from "../PackageDetailsModal";
import ProductSessionsSummary from "./ProductSessionSummary";
import { ProductTypeLabel } from "app/components/ProductTypeLabel";
import CancelPurchaseConfirmationModal from "app/components/CancelPurchaseConfirmationModal";
import { useBookingParticipants } from "app/routes/Sessions/useBookingParticipants";
import ParticipantPickerModal from "app/routes/Sessions/ParticipantPickerModal";

const PURCHASE_RETURN_PATH_KEY = "purchase_return_path";
// CN-1323 (CFTB-3303): the success URL is built on the BE and can't carry the
// recipient, so stash the dependent's name here right before the Stripe
// redirect; the post-redirect success modal reads and clears it.
export const PURCHASE_FAMILY_MEMBER_KEY = "purchase_family_member";

export default function AthleteOffering({
  product,
  returnPath,
}: {
  product: any;
  returnPath?: string;
}) {
  const { t } = useTranslation();
  const limitNote = offeringLimitNote(product, t);
  const scheduledStartText = product?.productType === "subscription"
    ? scheduledFirstChargeNote(
        product,
        `${currency_symbol[product.currency] ?? ""}${formatServerAmount(product?.price)}`,
        t,
      )
    : null;
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<React.ReactNode | null>(
    null,
  );
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  // CN-1323 (CFTB-3285): a head buying for a family member picks the recipient.
  const { candidates, hasFamily, refetch } = useBookingParticipants();

  // Open the "Who is this Package for?" picker when the athlete manages family
  // members; a regular athlete checks out for themselves directly.
  const startCheckout = () => {
    setDetailsModalOpen(false);
    if (hasFamily) {
      setPickerOpen(true);
    } else {
      handleCheckout(null);
    }
  };

  const handleCheckout = async (participantAthleteId: string | null = null) => {
    if (isPurchasing) {
      return;
    }
    setPickerOpen(false);
    setIsPurchasing(true);

    try {
      const data = await schedulingFetchPost(
        `/stripe_products/${product.id}/stripe_purchases`,
        participantAthleteId
          ? { participant_athlete_id: participantAthleteId }
          : {},
      );

      if (!data || !data.checkoutUrl) {
        throw new Error("Failed to get checkout URL.");
      }

      if (returnPath) {
        sessionStorage.setItem(PURCHASE_RETURN_PATH_KEY, returnPath);
      }

      // CN-1323 (CFTB-3303): the BE-built success URL can't name the recipient,
      // so carry the dependent's name across the redirect. Clear it for a
      // self-purchase so a prior family name never leaks into the modal.
      const recipient = participantAthleteId
        ? candidates.find((c) => c.id === participantAthleteId)
        : null;
      if (recipient) {
        sessionStorage.setItem(PURCHASE_FAMILY_MEMBER_KEY, recipient.name);
      } else {
        sessionStorage.removeItem(PURCHASE_FAMILY_MEMBER_KEY);
      }

      window.location.href = data.checkoutUrl;
    } catch (err) {
      setIsPurchasing(false);
      const error = err as any;
      console.error("Error creating purchase:", error);
      if (error?.errors && Array.isArray(error.errors)) {
        const existingSubError = error.errors.find(
          (e: any) => e.code === "existing_subscription",
        );
        if (existingSubError) {
          setErrorMessage(
            <span>
              {existingSubError.message ||
                t("You already have an active subscription.")}
              <br />
              <Link
                to="/account/athlete_subscriptions"
                style={{ fontWeight: "bold" }}
              >
                {t("View your subscriptions")}
              </Link>
            </span>,
          );
        } else {
          const message = error.errors
            .map((e: any) => e.message || e)
            .join(", ");
          setErrorMessage(message || t("An unexpected error has occurred."));
        }
      } else if (error?.error) {
        setErrorMessage(error.error);
      } else {
        setErrorMessage(t("An unexpected error has occurred."));
      }
    }
  };

  const buttonText = () => {
    if (product?.productType === "product") {
      return t("Buy Now");
    } else if (product.subscribed) {
      return t("Subscribed");
    } else {
      return t("Subscribe Now");
    }
  };

  // CN-2641: concurrent subscriptions for DIFFERENT products are allowed, so
  // the gate is per product — this offering's own `subscribed` flag — instead
  // of "any subscription with the coach".
  // CN-1323 (CFTB-3302): for a family account the recipient isn't known until
  // the picker resolves, so we don't pre-disable; the per-consumer, per-product
  // rule is enforced on the BE (CFTB-3285), whose `existing_subscription`
  // error is handled below. Solo accounts keep the pre-disable gate so a
  // self-subscriber can't double-subscribe to the same offering.
  const buttonDisabled =
    isPurchasing ||
    (!hasFamily &&
      product.productType === "subscription" &&
      product.subscribed === true);

  return (
    <>
      <div className={styles.product_card}>
        <div className={styles.product_body}>
          <div className={styles.product_status}>
            <ProductTypeLabel product={product} enabledIntegration={true} />
            <div className={styles.titleRow}>
              <div
                className={styles.truncatedText}
                title={product.title?.length > 27 ? product.title : ""}
              >
                {product.title?.length > 27
                  ? `${product?.title?.substring(0, 27)}...`
                  : product.title}
              </div>
              {product.title?.length > 27 && (
                <div className={styles.toolTipPosition}>
                  <ToolTip text={product.title} orientation="right" />
                </div>
              )}
            </div>
          </div>
          <ProductSessionsSummary
            product={product}
            onDetailsClick={() => setDetailsModalOpen(true)}
          />
        </div>
        <div className={styles.restriction_area}>
          {product.restrictWeekdays && !product.restrictWeekends && (
            <span className={styles.restriction_badge}>
              {weekdayRestrictionLabel(
                product.weekdayBeginAt ?? 0,
                product.weekdayEndAt ?? 1440,
                t("Weekdays only"),
              )}
            </span>
          )}
          {product.restrictWeekends && !product.restrictWeekdays && (
            <span className={styles.restriction_badge}>
              {t("Weekends & Holidays only")}
            </span>
          )}
        </div>
        {/* CFTB-3419: pinned directly above the separator rather than left to
            flow after the description — product_body is a fixed 300px, so a
            long description used to shove this line around or off the card.
            Emphasized because it sat in the same muted grey as the description
            and read as more description. */}
        {limitNote && <p className={styles.config_note}>{limitNote}</p>}
        <hr />

        <div className={styles.product_footer}>
          <div
            className={
              product.enabled
                ? styles.product_price
                : styles.product_price_inactive
            }
          >
            {currency_symbol[product.currency]}
            {formatServerAmount(product?.price)}
            {currency_symbol[product.currency] !==
              product.currency?.toUpperCase() &&
              ` ${product.currency?.toUpperCase()}`}
          </div>
          <p className={styles.payment_info}>
            {product?.productType === "product"
              ? t("One time payment")
              : product.recurring === "1"
                ? t("Billed monthly")
                : product.recurring === "12"
                  ? t("Billed annually")
                  : t("Billed every {{count}} months", {
                      count: product.recurring,
                    })}
          </p>
        </div>
        {/* CN-2832: a custom start date bills nothing at checkout. */}
        {scheduledStartText && <p className={styles.payment_info}>{scheduledStartText}</p>}
        <div className={styles.button_container}>
          <button
            disabled={buttonDisabled}
            className={cls("btn btn-primary", styles.offering_button, {
              ["disabled"]: buttonDisabled,
            })}
            onClick={startCheckout}
          >
            {" "}
            {buttonText()}
          </button>
        </div>
      </div>

      {detailsModalOpen && (
        <PackageDetailsModal
          product={product}
          isOpen={detailsModalOpen}
          onClose={() => setDetailsModalOpen(false)}
          buttonTitle="Purchase"
          onButtonClick={startCheckout}
        />
      )}

      <ParticipantPickerModal
        isOpen={pickerOpen}
        title={t("Who is this Package for?")}
        candidates={candidates}
        onClose={() => setPickerOpen(false)}
        onConfirm={(participantAthleteId) => handleCheckout(participantAthleteId)}
        onMemberAdded={refetch}
      />

      <CancelPurchaseConfirmationModal
        isOpen={errorMessage !== null}
        title={t("Error")}
        message={errorMessage || ""}
        isLoading={false}
        hideCancel={true}
        confirmLabel={t("OK")}
        onConfirm={() => setErrorMessage(null)}
        onCancel={() => setErrorMessage(null)}
      />
    </>
  );
}
